/**
 * Encode / decode JSON payloads as base64url for use in URL query parameters.
 * Uses UTF-8 throughout (safe for Unicode vs naive `btoa(JSON)`).
 */

/** Default cap on decoded UTF-8 size (bytes) before `JSON.parse` — avoids huge untrusted payloads. */
export const DEFAULT_MAX_DECODED_UTF8_BYTES = 65_536

/** Binary → base64url (no padding, URL-safe alphabet). */
export function uint8ToBase64Url(bytes: Uint8Array): string {
    let binary = ''
    const chunk = 0x8000
    for (let i = 0; i < bytes.length; i += chunk) {
        const sub = bytes.subarray(i, i + chunk)
        binary += String.fromCharCode(...sub)
    }
    const b64 = btoa(binary)
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** base64url → binary. */
export function base64UrlToUint8(encoded: string): Uint8Array {
    let b64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4
    if (pad > 0) {
        b64 += '='.repeat(4 - pad)
    }
    const binary = atob(b64)
    const out = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
        out[i] = binary.charCodeAt(i)
    }
    return out
}

export type EncodeJsonObjectBase64UrlOptions = {
    /** Passed to `JSON.stringify` when a number is set (stable key order if you pass a replacer). */
    replacer?: Parameters<typeof JSON.stringify>[1]
    space?: string | number | undefined
}

/**
 * `JSON.stringify` → UTF-8 → base64url.
 */
export function encodeJsonObjectBase64Url(
    value: unknown,
    options?: EncodeJsonObjectBase64UrlOptions,
): string {
    const json = JSON.stringify(value, options?.replacer, options?.space)
    const bytes = new TextEncoder().encode(json)
    return uint8ToBase64Url(bytes)
}

export type DecodeJsonObjectBase64UrlOptions = {
    /** Max UTF-8 byte length after base64 decode; rejects larger payloads. */
    maxDecodedUtf8Bytes?: number
    /** Passed to `JSON.parse`. */
    reviver?: Parameters<typeof JSON.parse>[1]
}

/**
 * base64url → UTF-8 → `JSON.parse`. Returns `null` on invalid input, oversize payload, or non‑JSON.
 */
export function decodeJsonObjectBase64Url<T = unknown>(
    encoded: string,
    options?: DecodeJsonObjectBase64UrlOptions,
): T | null {
    const maxBytes = options?.maxDecodedUtf8Bytes ?? DEFAULT_MAX_DECODED_UTF8_BYTES
    const trimmed = encoded.trim()
    if (!trimmed) {
        return null
    }
    // Rough bound: base64 expands binary ~4/3; reject obvious oversize early.
    if (trimmed.length > Math.ceil((maxBytes * 4) / 3) + 8) {
        return null
    }

    let bytes: Uint8Array
    try {
        bytes = base64UrlToUint8(trimmed)
    } catch {
        return null
    }

    if (bytes.length > maxBytes) {
        return null
    }

    try {
        const json = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
        return JSON.parse(json, options?.reviver) as T
    } catch {
        return null
    }
}
